import { Booking, BookingInput, BookingStatus } from '../types/booking';

const syncUrl = process.env.REACT_APP_GOOGLE_CALENDAR_SYNC_URL ?? '';
const syncSecret = process.env.REACT_APP_GOOGLE_CALENDAR_SYNC_SECRET ?? '';

export type CalendarSyncBooking = BookingInput & {
  id: string;
  status: BookingStatus;
  googleCalendarEventId?: string;
};

function isConfigured(): boolean {
  return Boolean(syncUrl && syncSecret);
}

type SyncResponse = {
  ok?: boolean;
  eventId?: string | null;
  deleted?: boolean;
  error?: string;
};

function parseSyncResponse(text: string, status: number): SyncResponse {
  try {
    return JSON.parse(text) as SyncResponse;
  } catch {
    throw new Error(
      `Risposta sync calendario non valida (${status}): ${text.slice(0, 120)}`
    );
  }
}

/**
 * Apps Script Web App: il redirect POST spesso diventa GET e perde il body.
 * Prova POST, poi fallback GET ?payload=
 */
async function postToAppsScript(
  body: Record<string, unknown>
): Promise<SyncResponse> {
  const postRes = await fetch(syncUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify(body),
    redirect: 'follow',
  });
  const postText = await postRes.text();

  try {
    return parseSyncResponse(postText, postRes.status);
  } catch {
    const sep = syncUrl.includes('?') ? '&' : '?';
    const getUrl = `${syncUrl}${sep}payload=${encodeURIComponent(
      JSON.stringify(body)
    )}`;
    const getRes = await fetch(getUrl, {
      method: 'GET',
      redirect: 'follow',
    });
    return parseSyncResponse(await getRes.text(), getRes.status);
  }
}

/**
 * Crea/aggiorna l’evento su Google Calendar (Apps Script).
 * Restituisce l’eventId, oppure null se eliminato / non configurato.
 */
export async function syncBookingToGoogleCalendar(
  booking: CalendarSyncBooking
): Promise<string | null> {
  if (!isConfigured()) {
    console.warn(
      'Google Calendar sync non configurato: mancano REACT_APP_GOOGLE_CALENDAR_SYNC_* in .env'
    );
    return booking.googleCalendarEventId ?? null;
  }

  if (booking.status === 'rejected') {
    const data = await postToAppsScript({
      secret: syncSecret,
      action: 'delete',
      id: booking.id,
      status: booking.status,
      googleCalendarEventId: booking.googleCalendarEventId ?? '',
    });
    if (!data.ok) {
      throw new Error(data.error || 'Eliminazione evento Google fallita');
    }
    return null;
  }

  const data = await postToAppsScript({
    secret: syncSecret,
    action: 'upsert',
    id: booking.id,
    status: booking.status,
    gruppoScout: booking.gruppoScout,
    branca: booking.branca,
    numeroRagazzi: booking.numeroRagazzi,
    numeroAdulti: booking.numeroAdulti,
    referenteNome: booking.referenteNome,
    telefono: booking.telefono,
    email: booking.email,
    startDate: booking.startDate,
    endDate: booking.endDate,
    startTime: booking.startTime,
    endTime: booking.endTime,
    altreInfo: booking.altreInfo,
    googleCalendarEventId: booking.googleCalendarEventId ?? '',
  });

  if (!data.ok) {
    throw new Error(data.error || 'Sync Google Calendar fallito');
  }

  return data.eventId ?? null;
}

/** Helper tipizzato da un Booking completo. */
export function toCalendarSyncBooking(booking: Booking): CalendarSyncBooking {
  return {
    id: booking.id,
    gruppoScout: booking.gruppoScout,
    branca: booking.branca,
    numeroRagazzi: booking.numeroRagazzi,
    numeroAdulti: booking.numeroAdulti,
    referenteNome: booking.referenteNome,
    telefono: booking.telefono,
    email: booking.email,
    startDate: booking.startDate,
    endDate: booking.endDate,
    startTime: booking.startTime,
    endTime: booking.endTime,
    altreInfo: booking.altreInfo,
    status: booking.status,
    googleCalendarEventId: booking.googleCalendarEventId,
  };
}
